import type { FilmstripBeatCore } from "@vms/contracts";
// api/ops.ts — the T4 operator-UI API (Run Inspector + per-step actions + run
// lifecycle + re-stitch + artefacts/casting). All under /api/ops, requireAdmin.
// The backend proxies these to the workers /ops (behind OPS_TOKEN) and the CMS.
import { apiGet, apiPost } from "./client.ts";
import type { RulesCardRead, RulesCardSaved } from "@/lib/rules-card.ts";   // (tf3-38)
import { confirmingFreeVideo } from "./free-video.ts";   // (session 10, A7)

export interface RunStep {
  rowId: string;
  stepId: string;
  beatId: string | null;
  status: string;
  attemptNumber: number;
  failureCategory: string | null;
  respecificationReason: string | null;
  respecificationCount: number;
  providerModule: string | null;
  outputUrl: string | null;
  score: string | null;
  dispatchedAt: string | null;
  endedAt: string | null;
  durationSec: number | null;
}

export interface RunInspector {
  run: { id: string; videoId: string | null; workflowId: string; status: string; cmsId: string | null;
    /** (session 9) the order's intake warnings — empty when it had none (or from workers before this drop: absent) */
    intakeWarnings?: string[];
    /** (tf3-35b; zip 46) a dry run (stops before its first clip), and when it stopped — the workers' run row */
    dryRunBeforeClips?: boolean; dryRunStoppedAt?: string | null;
    /** (B119; C2.22) a rehearsal (stand-in clips), and when its render and sound_verify ended — the workers' run row */
    rehearsal?: boolean; rehearsalRenderedAt?: string | null } | null;
  stepCount: number;
  steps: RunStep[];
}

export interface NeedsAttentionItem {
  rowId: string;
  runId: string;
  stepId: string;
  beatId: string | null;
  status: string;
  attemptNumber: number;
  failureCategory?: string | null;
  /** The run it belongs to (workers ≥ this drop): what it is, not just an id. */
  run?: { status: string; workflowId: string; createdAt: string | null; videoId: string | null };
}

export interface BeatProduction {
  beatId: string;
  image?: { url: string | null; status: string | null };
  endImage?: { url: string | null; status: string | null };
  card?: { url: string | null; status: string | null };
  clip?: { url: string | null; status: string | null; durationSec?: number | null };
  resolvedDurationSec?: number | null;
  clipDurationSec?: number | null;
}

export interface CompletedJob {
  runId: string;
  videoId: string | null;
  cmsId: string | null;
  workflowId: string;
  finalVideoUrl: string | null;
  videoStatus: string | null;
  endedAt: string | null;
  /** (session 10, A3: session 8's F1, lost) the run's tenant -- who finishes it (engine-console-rules finishingSide). The
   *  workers send it; the page dropped it, so every run's Finish & Post went to the backend. */
  tenantId?: string;
}

const BASE = "/ops";



// ── G5: the filmstrip — one read (workers GET /ops/runs/:runId/filmstrip, via the backend proxy). The shapes mirror
//    the two layers: the CMS projection (beats by segment, production plane, previews by CURRENT url, the animatic)
//    and the workers overlay (each beat's current step / cost / per-step status; the run's parked gate). ──
export interface Preview { posterUrl: string; thumbUrl: string; stripUrl: string | null }
export interface StepBadge { stepId: string; status: string; score: number | null; spentUsdCents: number; attempt: number;
  /** Operator visuals G3: a verify step's verdict and its defect (null for other steps). */
  passed?: boolean | null; defect?: string | null }
export interface BeatOverlay { currentStep: string; spentUsdCents: number; steps: StepBadge[] }
export interface FilmstripTake { index: number; status: string; url: string | null; durationSec: number | null }
// (session 6: every layer carries contracts' FilmstripBeatCore — beatId, position, name, intent, spokenText, shotType —
//  required, so a layer that drops one fails to typecheck; the K-1 class)
export interface FilmstripBeat extends FilmstripBeatCore {
  production: {
    image: { url: string | null; status: string | null; score: number | null } | null;
    clip: { url: string | null; status: string | null; score: number | null } | null;
    takePlan?: FilmstripTake[];
    alignmentConfidence?: number | null;
    clipWindow?: { requestedSec: number | null; deliveredSec: number; windowSec: number | null; holdSec: number | null } | null;
    /** Operator visuals G1/G2/G4: the end frame, the card, and the beat's window in the voice track. */
    endImage?: { url: string | null; status: string | null } | null;
    card?: { url: string | null; status: string | null } | null;
    sliceStartMs?: number | null; sliceMs?: number | null; conformedStartMs?: number | null; conformedEndMs?: number | null;
  } | null;
  previews: {
    frame: Preview | null; endFrame: Preview | null; card: Preview | null; clip: Preview | null;
    takes: { index: number; preview: Preview | null }[]; beatCrop: Preview | null;
  };
  run?: BeatOverlay;
}
export interface Filmstrip {
  cmsId: string;
  segments: { id: string; orderIndex: number; beats: FilmstripBeat[] }[];
  document: {
    voice: { url: string | null; conformedUrl: string | null; trackDurationMs: number | null } | null;
    render: { url: string; status: string | null; durationSec: number | null; preview: Preview | null } | null;
    animaticUrl: string | null;
    /** Operator visuals G5: post-production's re-stitch (CMS 0054). */
    postRender?: { url: string; status: string | null } | null;
  };
  run: {
    id: string; status: string; workflowId: string; spentUsdCents: number; budgetUsdCents: number;
    supersedesRunId: string | null; document: BeatOverlay; awaitingGate: { rowId: string; stepId: string } | null;
  };
  revisions?: { previousRunId: string; previousRun?: Filmstrip["run"]; beats?: { beatId: string; current: FilmstripBeat | null; previous: FilmstripBeat | null }[]; error?: string };
}

export const opsApi = {
  // G5: the run as pictures first
  filmstrip: (runId: string) => apiGet<Filmstrip>(`${BASE}/runs/${runId}/filmstrip`),
  // (tf3-38; C2.0-2 §3) the rules card at the storyboard: read it; a person's edit (the backend names the person)
  rulesCard: (runId: string) => apiGet<RulesCardRead>(`${BASE}/runs/${runId}/rules-card`),
  editRulesCard: (runId: string, edit: Record<string, unknown>, reason?: string) =>
    apiPost<RulesCardSaved>(`${BASE}/runs/${runId}/rules-card`, { edit, ...(reason?.trim() ? { reason: reason.trim() } : {}) }),
  // G5 Re-roll the layer shown: a frame (bounded at the storyboard gate) or a take (and the takes chained after it)
  regenerateFrame: (runId: string, beatId: string) =>
    apiPost<{ ok: boolean; reason: string; code?: string }>(`${BASE}/runs/${runId}/beats/${beatId}/regenerate-for-review`, {}),
  retakeTake: (runId: string, beatId: string, k: number) =>
    apiPost<{ ok: boolean; reason: string }>(`${BASE}/runs/${runId}/beats/${beatId}/takes/${k}/retake`, {}),
  // G5: the RUN's gate (gates are per run) — the existing row-keyed decide
  decideGate: (rowId: string, approved: boolean, note?: string) =>
    apiPost<{ ok: boolean; reason: string; code?: string }>(`${BASE}/gates/${rowId}/decide`, { approved, ...(note ? { note } : {}) }),
  // completed jobs (post-production handoff list)
  completedRuns: async (opts?: { limit?: number; withVideoOnly?: boolean }): Promise<CompletedJob[]> => {
    const p = new URLSearchParams();
    if (opts?.limit) p.set("limit", String(opts.limit));
    if (opts?.withVideoOnly) p.set("withVideoOnly", "1");
    const q = p.toString() ? `?${p.toString()}` : "";
    const r = await apiGet<{ items?: CompletedJob[] } | CompletedJob[]>(`${BASE}/runs/completed${q}`);
    return Array.isArray(r) ? r : (r?.items ?? []);
  },

  // Run Inspector (item 1)
  runSteps: (runId: string) => apiGet<RunInspector>(`${BASE}/runs/${runId}/steps`),

  // needs-attention (item 5)
  needsAttention: async (runId?: string): Promise<NeedsAttentionItem[]> => {
    const q = runId ? `?run=${encodeURIComponent(runId)}` : "";
    const r = await apiGet<{ items?: NeedsAttentionItem[] } | NeedsAttentionItem[]>(`${BASE}/needs-attention${q}`);
    return Array.isArray(r) ? r : (r?.items ?? []);
  },

  // per-step actions (item 2)
  retryStep: (rowId: string) => confirmingFreeVideo((q) => apiPost<{ ok: boolean; reason: string }>(`${BASE}/steps/${rowId}/retry${q}`, {})),
  skipStep: (rowId: string, reason?: string) =>
    confirmingFreeVideo((q) => apiPost<{ ok: boolean; reason: string }>(`${BASE}/steps/${rowId}/skip${q}`, reason ? { reason } : {})),
  // tf-041 (Track D): always human-triggered — the Production tab shows this only
  // on identity/redress-related step failures, never as a default action.
  reseedStep: (rowId: string, cmsId: string, beatId: string) =>
    apiPost<{ ok: boolean; reason: string; newVariantStockId?: string; retiredVariantStockId?: string }>(
      `${BASE}/steps/${rowId}/reseed`, { cmsId, beatId },
    ),
  reRenderBeat: (runId: string, beatId: string, stepIds?: string[]) =>
    apiPost<{ ok: boolean; reason: string }>(`${BASE}/runs/${runId}/beats/${beatId}/re-render`, stepIds ? { stepIds } : {}),
  // Session 6 T2: a soft drop (the voice re-conformed without the beat) and its mirror
  dropBeat: (runId: string, beatId: string) =>
    apiPost<{ ok: boolean; reason: string; mode?: "reconform" | "revoice"; stepsReset?: number }>(`${BASE}/runs/${runId}/beats/${beatId}/drop`, {}),
  // Session 6 T3: the operator's comparison (with cost)
  compare: (runId: string) => apiGet<import("../components/engine/CompareExecutions").CompareData>(`${BASE}/runs/${runId}/compare`),
  compareChoose: (runId: string, executionIndex: number) => apiPost<{ ok: boolean; reason: string }>(`${BASE}/runs/${runId}/compare/choose`, { executionIndex }),
  compareReject: (runId: string) => apiPost<{ ok: boolean; reason: string }>(`${BASE}/runs/${runId}/compare/reject`, {}),
  droppedBeats: (runId: string) =>
    apiGet<{ beats: { beatId: string; name: string | null; position: number | null; dropped: { at: string; by: string | null } }[] }>(`${BASE}/runs/${runId}/dropped-beats`),
  restoreBeat: (runId: string, beatId: string) =>
    apiPost<{ ok: boolean; reason: string; unskipped?: number; stepsReset?: number }>(`${BASE}/runs/${runId}/beats/${beatId}/restore`, {}),
  cascade: (runId: string, fromStepId: string, beatId?: string) =>
    apiPost<{ ok: boolean; reason: string }>(`${BASE}/runs/${runId}/cascade/${fromStepId}`, beatId ? { beatId } : {}),

  // Phase 4 — edit-then-continue: reject the gate, patch the facet, cascade. One call.
  editContinue: (runId: string, body: {
    gateRowId?: string; facet: string; cmsId: string; segmentId?: string;
    value?: unknown; beatId?: string; reviewerId: string;
  }) =>
    apiPost<{ step: string; facet?: string; cascadedFrom?: string; ok?: boolean; reason?: string; error?: string }>(
      `${BASE}/runs/${runId}/edit-continue`, body),

  // re-stitch (item 4)
  reStitch: (runId: string) => apiPost<{ ok: boolean; reason: string; renderStepsReset: number }>(`${BASE}/runs/${runId}/re-stitch`, {}),

  // run lifecycle (item 3)
  revive: (runId: string) => confirmingFreeVideo((q) => apiPost<{ ok: boolean; reason: string }>(`${BASE}/runs/${runId}/revive${q}`, {})),

  // artefacts (item 6) — CMS-proxied
  /** Operator visuals G6: the document's retention curves, one per published post. */
  retentionCurve: (cmsId: string) =>
    apiGet<{ curves: { platform: string; postId: string; points: { second: number; retained: number }[] }[] }>(`${BASE}/cms/${cmsId}/retention-curve`),
  beatProduction: (cmsId: string, beatId: string) => apiGet<BeatProduction>(`${BASE}/cms/${cmsId}/beats/${beatId}/production`),
  // casting (item 7) — CMS-proxied
  avatars: (cmsId: string) => apiGet<unknown>(`${BASE}/cms/${cmsId}/avatars`),
  // tf-041 UX: a document's segments (for the edit-cast segment picker). Each carries
  // its id + current castRefs, so the panel can show which segments exist and what
  // they're currently cast as, instead of asking the operator to type a segment id.
  cmsSegments: (cmsId: string) =>
    apiGet<{ segments: Array<{ id: string; orderIndex: number; castRefs: string[] | null }> }>(`${BASE}/cms/${cmsId}/segments`),
};
